import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createAssertion,
  type Assertion,
  type Condition,
  type ConditionKind,
  type Method,
  type PropertyDefinition
} from '@peruselab/api-client/assertions';
import { DiscussionError, type Reference } from '@peruselab/api-client/discussions';
import { ReferencePicker } from '../../discussions/ReferencePicker';
import { SolventPicker } from './SolventPicker';
import { Icon } from '../../../components/Icon';

const CONDITION_KINDS: readonly ConditionKind[] = ['temperature', 'pressure', 'ph', 'solvent'];
/** The quantity conditions' dimensions; a solvent (#920) is a Molecule, not a quantity. */
const CONDITION_DIMENSION: Record<ConditionKind, string> = {
  temperature: 'temperature',
  pressure: 'pressure',
  ph: 'dimensionless',
  solvent: ''
};
const METHODS: readonly Method['kind'][] = ['MEASURED', 'CALCULATED', 'PREDICTED', 'REPORTED'];

interface AddValueFormProps {
  readonly moleculeId: string;
  readonly catalog: readonly PropertyDefinition[];
  /** Correcting one of your own values (D13): the form starts from it and supersedes it. */
  readonly correcting?: Assertion | null;
  /** #931: the property the form starts on (the evidence view's current one). */
  readonly initialPropertyId?: string | undefined;
  readonly onDone: (created: Assertion) => void;
  readonly onCancel: () => void;
}

/** Units by dimension, from the catalog itself -- conditions use the same reviewed table (D4). */
function unitsByDimension(catalog: readonly PropertyDefinition[]) {
  const byDimension = new Map<string, readonly { code: string; symbol: string }[]>();
  for (const property of catalog)
    if (!byDimension.has(property.dimension)) byDimension.set(property.dimension, property.units);
  if (!byDimension.has('dimensionless')) byDimension.set('dimensionless', [{ code: '1', symbol: '' }]);
  return byDimension;
}

/**
 * ADR 0018 phase 1 (#915): add a property value -- a value or range in a unit that fits the property,
 * its conditions, its method, and its evidence (references, or "my unpublished result", D8), public
 * or private (D12). Nothing is guessed: the server refuses what doesn't fit, and says why.
 */
export function AddValueForm({
  moleculeId,
  catalog,
  correcting,
  initialPropertyId,
  onDone,
  onCancel
}: AddValueFormProps) {
  const { t } = useTranslation();
  const formId = useId();
  const units = useMemo(() => unitsByDimension(catalog), [catalog]);
  const [propertyId, setPropertyId] = useState(correcting?.propertyId ?? initialPropertyId ?? catalog[0]?.id ?? '');
  const property = catalog.find((candidate) => candidate.id === propertyId);
  const [value, setValue] = useState(correcting?.value.value ?? '');
  const [isRange, setIsRange] = useState(Boolean(correcting?.value.upper));
  const [upper, setUpper] = useState(correcting?.value.upper ?? '');
  const [unit, setUnit] = useState(
    correcting?.value.unit ?? property?.units[property.units.length > 1 ? 1 : 0]?.code ?? ''
  );
  const [uncertainty, setUncertainty] = useState(correcting?.value.uncertainty ?? '');
  const [conditions, setConditions] = useState<readonly Condition[]>(
    correcting?.conditions.map(({ kind, value: conditionValue, unit: conditionUnit }) => ({
      kind,
      value: conditionValue,
      unit: conditionUnit
    })) ?? []
  );
  const [methodKind, setMethodKind] = useState<Method['kind']>(correcting?.method.kind ?? 'MEASURED');
  const [detail, setDetail] = useState(correcting?.method.detail ?? '');
  const [confidence, setConfidence] = useState(
    correcting?.method.confidence != null ? String(correcting.method.confidence) : ''
  );
  const [references, setReferences] = useState<readonly Reference[]>(correcting?.references ?? []);
  const [unpublished, setUnpublished] = useState(correcting?.unpublished ?? false);
  const [note, setNote] = useState(correcting?.unpublishedNote ?? '');
  const [audience, setAudience] = useState<'PUBLIC' | 'PRIVATE'>(correcting?.audience ?? 'PUBLIC');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chooseProperty = (id: string) => {
    setPropertyId(id);
    const next = catalog.find((candidate) => candidate.id === id);
    setUnit(next?.units[next.units.length > 1 ? 1 : 0]?.code ?? '');
  };
  const unusedConditions = CONDITION_KINDS.filter((kind) => !conditions.some((condition) => condition.kind === kind));
  // °C for temperature and kPa for pressure: the second unit in each list, the one people usually write.
  const addCondition = (kind: ConditionKind) =>
    setConditions([
      ...conditions,
      kind === 'solvent'
        ? { kind, value: '', unit: '' }
        : { kind, value: '', unit: units.get(CONDITION_DIMENSION[kind])?.[kind === 'ph' ? 0 : 1]?.code ?? '1' }
    ]);
  // #920: what a chosen solvent is called, for the picker (the server stores its own label).
  const [solventLabel, setSolventLabel] = useState<string | null>(
    correcting?.conditions.find((condition) => condition.kind === 'solvent')?.label ?? null
  );
  const updateCondition = (index: number, change: Partial<Condition>) =>
    setConditions(conditions.map((condition, at) => (at === index ? { ...condition, ...change } : condition)));
  const hasEvidence = references.length > 0 || unpublished;

  return (
    <form
      className="add-value-form"
      aria-labelledby={`${formId}-title`}
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || !property || !value.trim() || !hasEvidence) return;
        setBusy(true);
        setError(null);
        createAssertion(moleculeId, {
          propertyId: property.id,
          value: {
            value: value.trim(),
            upper: isRange && upper.trim() ? upper.trim() : null,
            unit,
            uncertainty: uncertainty.trim() || null
          },
          conditions: conditions.filter((condition) => condition.value.trim()),
          method: {
            kind: methodKind,
            detail: detail.trim() || null,
            confidence: methodKind === 'PREDICTED' && confidence.trim() ? Number(confidence) : null
          },
          references: references.map((reference) => reference.id),
          unpublished,
          unpublishedNote: unpublished ? note.trim() || null : null,
          audience,
          supersedes: correcting?.id ?? null
        })
          .then(onDone)
          .catch((failure: unknown) => {
            setError(
              failure instanceof DiscussionError && failure.kind === 'invalid'
                ? failure.message
                : t(`discussions.errors.${failure instanceof DiscussionError ? failure.kind : 'unknown'}`)
            );
          })
          .finally(() => setBusy(false));
      }}
    >
      <h3 id={`${formId}-title`}>{correcting ? t('properties.form.correctTitle') : t('properties.form.title')}</h3>

      <label className="add-value-field">
        <span>{t('properties.form.property')}</span>
        <select
          value={propertyId}
          disabled={Boolean(correcting)}
          onChange={(event) => chooseProperty(event.target.value)}
        >
          {catalog.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="add-value-quantity">
        <legend>{t('properties.form.value')}</legend>
        <input
          aria-label={isRange ? t('properties.form.from') : t('properties.form.value')}
          inputMode="decimal"
          dir="ltr"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        {isRange ? (
          <input
            aria-label={t('properties.form.to')}
            inputMode="decimal"
            dir="ltr"
            value={upper}
            onChange={(event) => setUpper(event.target.value)}
          />
        ) : null}
        {property && property.units.length > 1 ? (
          <select aria-label={t('properties.form.unit')} value={unit} onChange={(event) => setUnit(event.target.value)}>
            {property.units.map((option) => (
              <option key={option.code} value={option.code}>
                {option.symbol}
              </option>
            ))}
          </select>
        ) : null}
        <input
          aria-label={t('properties.form.uncertainty')}
          placeholder="±"
          inputMode="decimal"
          dir="ltr"
          className="add-value-uncertainty"
          value={uncertainty}
          onChange={(event) => setUncertainty(event.target.value)}
        />
        <label className="add-value-check">
          <input type="checkbox" checked={isRange} onChange={(event) => setIsRange(event.target.checked)} />
          <span>{t('properties.form.range')}</span>
        </label>
      </fieldset>

      <fieldset className="add-value-conditions">
        <legend>{t('properties.form.conditions')}</legend>
        {conditions.map((condition, index) => {
          const options = units.get(CONDITION_DIMENSION[condition.kind]) ?? [];
          if (condition.kind === 'solvent') {
            return (
              <div key={condition.kind} className="add-value-condition">
                <span>{t('properties.conditions.solvent')}</span>
                <SolventPicker
                  value={condition.value}
                  label={solventLabel}
                  onChange={(chosen, name) => {
                    setSolventLabel(name);
                    updateCondition(index, { value: chosen });
                  }}
                />
                <button
                  type="button"
                  className="discussion-link-button"
                  onClick={() => setConditions(conditions.filter((_, at) => at !== index))}
                >
                  {t('properties.form.removeCondition')}
                </button>
              </div>
            );
          }
          return (
            <div key={condition.kind} className="add-value-condition">
              <span>{t(`properties.conditions.${condition.kind}`)}</span>
              <input
                aria-label={t(`properties.conditions.${condition.kind}`)}
                inputMode="decimal"
                dir="ltr"
                value={condition.value}
                onChange={(event) => updateCondition(index, { value: event.target.value })}
              />
              {options.length > 1 ? (
                <select
                  aria-label={t('properties.form.conditionUnit', {
                    condition: t(`properties.conditions.${condition.kind}`)
                  })}
                  value={condition.unit}
                  onChange={(event) => updateCondition(index, { unit: event.target.value })}
                >
                  {options.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.symbol}
                    </option>
                  ))}
                </select>
              ) : null}
              <button
                type="button"
                className="discussion-link-button"
                onClick={() => setConditions(conditions.filter((_, at) => at !== index))}
              >
                {t('properties.form.removeCondition')}
              </button>
            </div>
          );
        })}
        {unusedConditions.length > 0 ? (
          <div className="add-value-condition-add">
            {unusedConditions.map((kind) => (
              <button
                key={kind}
                type="button"
                className={`discussion-link-button${property?.usualConditions.includes(kind) ? ' add-value-usual' : ''}`}
                onClick={() => addCondition(kind)}
              >
                {t('properties.form.addCondition', { condition: t(`properties.conditions.${kind}`) })}
              </button>
            ))}
          </div>
        ) : null}
      </fieldset>

      <fieldset className="add-value-method">
        <legend>{t('properties.form.method')}</legend>
        <select
          aria-label={t('properties.form.method')}
          value={methodKind}
          onChange={(event) => setMethodKind(event.target.value as Method['kind'])}
        >
          {METHODS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`properties.methods.${kind}`)}
            </option>
          ))}
        </select>
        <input
          aria-label={t(`properties.form.detail.${methodKind}`)}
          placeholder={t(`properties.form.detail.${methodKind}`)}
          dir="auto"
          maxLength={200}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
        />
        {methodKind === 'PREDICTED' ? (
          <input
            aria-label={t('properties.form.confidence')}
            placeholder={t('properties.form.confidence')}
            inputMode="decimal"
            dir="ltr"
            className="add-value-uncertainty"
            value={confidence}
            onChange={(event) => setConfidence(event.target.value)}
          />
        ) : null}
      </fieldset>

      <fieldset className="add-value-evidence">
        <legend>{t('properties.form.evidence')}</legend>
        <ReferencePicker references={references} onChange={setReferences} disabled={busy} />
        <label className="add-value-check">
          <input type="checkbox" checked={unpublished} onChange={(event) => setUnpublished(event.target.checked)} />
          <span>{t('properties.form.unpublished')}</span>
        </label>
        {unpublished ? (
          <input
            aria-label={t('properties.form.unpublishedNote')}
            placeholder={t('properties.form.unpublishedNote')}
            dir="auto"
            maxLength={500}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        ) : null}
        {!hasEvidence ? <p className="discussion-note">{t('properties.form.evidenceNeeded')}</p> : null}
      </fieldset>

      <fieldset className="add-value-audience">
        <legend>{t('properties.form.audience')}</legend>
        {(['PUBLIC', 'PRIVATE'] as const).map((choice) => (
          <label key={choice} className="add-value-check">
            <input
              type="radio"
              name={`${formId}-audience`}
              checked={audience === choice}
              onChange={() => setAudience(choice)}
            />
            <span>{t(`properties.audience.${choice}`)}</span>
          </label>
        ))}
      </fieldset>

      {error ? (
        <p className="import-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="comment-composer-footer">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="icon-button"
          aria-label={t('discussions.cancel')}
          title={t('discussions.cancel')}
        >
          <Icon name="close" />
        </button>
        <button
          type="submit"
          className="icon-button"
          disabled={busy || !value.trim() || !hasEvidence}
          aria-label={correcting ? t('properties.form.saveCorrection') : t('properties.form.add')}
          title={correcting ? t('properties.form.saveCorrection') : t('properties.form.add')}
        >
          <Icon name="check" />
        </button>
      </div>
    </form>
  );
}
